import { fingerprintValue } from './quote-snapshot-diff';

type UnknownRecord = Record<string, unknown>;

export interface ProjectSummaryFocusRecoveryIdentity {
    projectId?: unknown;
    loadGeneration?: unknown;
    evidenceFingerprint?: unknown;
    apiKey?: unknown;
    model?: unknown;
    configurationRevision?: unknown;
    failure?: unknown;
}

function record(value: unknown): UnknownRecord {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : {};
}

function normaliseInteger(value: unknown): number | null {
    const numeric = Number(value);
    return Number.isSafeInteger(numeric) && numeric >= 0 ? numeric : null;
}

/**
 * Stable family for a provider/configuration failure. Volatile prose and
 * timestamps are deliberately excluded: a provider changing its wording must
 * not mint another passive retry. A materially different classified failure
 * can receive its own single recovery attempt.
 */
export function projectSummaryFailureFamily(value: unknown): string {
    const failure = record(value);
    const category = String(failure.category || '').trim().toLowerCase();
    const code = String(failure.code || '').trim().toLowerCase();
    const statusCode = Number(failure.statusCode || failure.status || 0) || 0;
    const retryClass = failure.retryable === false ? 'terminal' : 'retryable';
    return JSON.stringify([
        category || 'unclassified',
        code || 'uncoded',
        statusCode ? Math.floor(statusCode / 100) : 0,
        retryClass,
    ]);
}

/**
 * Identity for the one browser-focus recovery allowed after a failed automatic
 * Project Summary. The ordinary recovery revision is intentionally absent:
 * focus itself increments that revision, so including it would let each focus
 * event manufacture a fresh paid attempt forever.
 */
export function buildProjectSummaryFocusRecoveryKey({
    projectId,
    loadGeneration,
    evidenceFingerprint,
    apiKey,
    model,
    configurationRevision,
    failure,
}: ProjectSummaryFocusRecoveryIdentity = {}): string {
    const evidence = String(evidenceFingerprint || '').trim();
    const load = normaliseInteger(loadGeneration);
    if (!failure || !evidence || load === null) return '';

    return `project-summary-focus-recovery-v1:${fingerprintValue({
        projectId: String(projectId || 'new-project'),
        loadGeneration: load,
        evidenceFingerprint: evidence,
        // Hash the browser-direct credential independently so no key material
        // appears in logs/receipts while a real credential change still re-arms.
        providerKeyFingerprint: fingerprintValue(String(apiKey || 'gateway')),
        model: String(model || ''),
        configurationRevision: normaliseInteger(configurationRevision) ?? 0,
        failureFamily: projectSummaryFailureFamily(failure),
    })}`;
}

export function claimProjectSummaryFocusRecovery(
    receipts: Set<string>,
    identity: ProjectSummaryFocusRecoveryIdentity = {},
): boolean {
    const key = buildProjectSummaryFocusRecoveryKey(identity);
    if (!key || receipts.has(key)) return false;
    receipts.add(key);
    return true;
}
